import { ensureArray } from '@fox/atoms/utils/array';
import { filterEmpty } from '@fox/atoms/utils/filter-empty';
import { tryParseJSONArray, tryParseJSONObject } from '@fox/atoms/utils/string';
import { IWorkBenchLayout } from '@fox/blocks/contracts/work-bench';
import traverse from 'neotraverse';
import { TICKET_BLOCK_TYPE } from '../../../../form-block-types';
import { IField, IQuestionAnswer, QuestionsType } from '../../../form-root/contracts';
import { FormRenderMode } from '../../../form-runtime-context/contracts';
import { useFormRuntimeContext } from '../../../form-runtime-context/form-runtime-context';
import { CreatePersonBlockMetadata } from '../../create-person-block/contracts';
import { FormQuestionTypes, ProvidesSelect, ProvidesSubQuestion, QuestionBlockMetadata } from '../../question-block/contracts';
import { ISection } from '../../section-block/contracts';
import { SubscriptionFieldMeta } from '../../subscription-block/contracts';
import {
	ConditionalBlockMeta,
	ConditionalDefinition,
	ConditionalDefinitions,
	ConditionOperators,
	ConditionResult,
	LegacyJsonConditionType
} from './contracts';

// Performance caches - aggressive caching system for massive forms
const conditionCache = new Map<string, ConditionResult>();
const questionsCache = new Map<string, QuestionsType[]>();
const columnConditionsCache = new Map<string, ConditionalDefinition[]>();
const layoutBlocksCache = new Map<string, IField[]>();

let cacheVersion = 0;

/**
 * Clear all performance caches when form structure changes
 */
export const clearConditionCaches = (): void => {
	conditionCache.clear();
	questionsCache.clear();
	columnConditionsCache.clear();
	layoutBlocksCache.clear();
	cacheVersion++;
};

/**
 * Clear only answer-dependent caches when answers change (form structure stays the same)
 */
export const clearAnswerDependentCaches = (): void => {
	// Only clear caches that depend on answers, not form structure
	conditionCache.clear();
	// Don't clear questionsCache, columnConditionsCache, layoutBlocksCache as they depend on form structure, not answers
	cacheVersion++;
};

/**
 * Cache key generation - minimal overhead
 */
const lightKey = (...parts: (string | number | boolean)[]): string => {
	return `${cacheVersion}:${parts.join(':')}`;
};

/**
 * Simple hash function for unique signatures - faster than crypto but still unique
 */
const simpleHash = (str: string): string => {
	let hash = 0;
	if (str.length === 0) return '0';
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash).toString(36);
};

/**
 * Create a unique signature from answers - lightweight but always unique
 */
const answersSignature = (answers: IQuestionAnswer[]): string => {
	if (!answers || answers.length === 0) return '0';

	// Create a compact but unique representation
	// Map each answer to question:answer format, sort for consistency, then hash
	const answerStrings = answers.map(a => `${a.questionName}:${JSON.stringify(a.answer || null)}`).sort(); // Sort to ensure consistent ordering

	// Join and hash to create unique but compact signature
	const combined = answerStrings.join('|');
	return simpleHash(combined);
};

/**
 * Generate optimized cache key for condition evaluation
 */
const generateConditionCacheKey = (
	questionName: string,
	conditionalDefinitions: ConditionalDefinitions | undefined,
	answers: IQuestionAnswer[],
	formRenderMode: FormRenderMode
): string => {
	// Ultra-light cache key - just basic signatures
	const conditionsCount = conditionalDefinitions ? JSON.stringify(conditionalDefinitions).length.toString() : '0';
	return lightKey(questionName, formRenderMode.toString(), answersSignature(answers), conditionsCount);
};

function validateMultiChoiceSelects(conditionalDefinitions, answerArray) {
	return conditionalDefinitions.selects?.some(s => answerArray.includes(s));
}

/**
 * Optimized cached version of findAllFormQuestionSelects
 */
const findAllFormQuestionSelectsCached = (sections: ISection[] | IField[]): QuestionsType[] => {
	// Ultra-light cache key - just use array length and structure indicator
	const sectionsSignature = sections.length.toString();
	const sectionsKey = lightKey('sections', sectionsSignature);

	if (questionsCache.has(sectionsKey)) {
		return questionsCache.get(sectionsKey)!;
	}

	const result = findAllFormQuestionSelects(sections);
	questionsCache.set(sectionsKey, result);
	return result;
};

/**
 * Optimized cached version of findColumnConditions
 */
const findColumnConditionsCached = (
	questionName: string,
	layout: IWorkBenchLayout | undefined,
	existingConditions: ConditionalDefinition[]
): ConditionalDefinition[] => {
	// Ultra-light cache key - just use simple indicators
	const layoutIndicator = layout ? 'layout' : '0';
	const conditionsCount = existingConditions.length.toString();
	const layoutKey = lightKey('column', questionName, layoutIndicator, conditionsCount);

	if (columnConditionsCache.has(layoutKey)) {
		return columnConditionsCache.get(layoutKey)!;
	}

	const result = findColumnConditions(questionName, layout, existingConditions);
	columnConditionsCache.set(layoutKey, result);
	return result;
};

/**
 * Optimized cached version of getQuestionsAndSelects
 */
const getQuestionsAndSelectsCached = (
	sections: ISection[] | IField[],
	questionId: string
): { questions: QuestionsType[]; allProvidesSelects: ProvidesSelect } => {
	const questions = findAllFormQuestionSelectsCached(sections)?.filter(q => q.name === questionId);
	const allProvidesSelects: ProvidesSelect = filterEmpty(questions?.flatMap(q => (q.metadataJson as any)?.provides?.select));
	return { questions, allProvidesSelects };
};

/**
 * Make sure the question that this condition depends on is valid as well
 * @param questions - All questions that work with conditions
 * @param conditionQuestionId - questionId condition depends on
 * @param sections - Form sections
 * @param answers - Form answers
 * @param depth - Loop depth
 * @param layout - Form layout
 * @returns ConditionResult of valid/invalid/loop
 */
function checkMatchConditionValid(
	questions: QuestionsType[],
	conditionQuestionId: string,
	sections: ISection[] | IField[],
	answers: IQuestionAnswer[],
	depth: number,
	layout: IWorkBenchLayout | undefined,
	questionName: string
) {
	// Early return for deep recursion
	if (depth > 100) return ConditionResult.Loop;

	// Ultra-light cache key for validation
	const cacheKey = lightKey('match', conditionQuestionId, depth.toString(), answersSignature(answers), questionName);
	if (conditionCache.has(cacheKey)) {
		return conditionCache.get(cacheKey)!;
	}

	// check for conditions on this question
	const matchConditions = filterEmpty(ensureArray(questions.find(q => q.name === conditionQuestionId)?.metadataJson?.conditionalDefinitions));

	// check if this block exists inside a column incase the column is conditional
	const columnConditions = findColumnConditionsCached(conditionQuestionId, layout, matchConditions);
	matchConditions.push(...columnConditions);

	let isValidMatch: ConditionResult;
	if (!matchConditions || matchConditions.length === 0) {
		isValidMatch = ConditionResult.Valid;
	} else {
		const results = matchConditions.map(definitions =>
			checkQuestionConditionValid(sections, definitions, answers, depth + 1, layout, questionName)
		);
		if (results.some(r => r === ConditionResult.Loop)) {
			isValidMatch = ConditionResult.Loop;
		} else {
			isValidMatch = results.every(r => r === ConditionResult.Valid) ? ConditionResult.Valid : ConditionResult.Invalid;
		}
	}

	// Cache the result
	conditionCache.set(cacheKey, isValidMatch);
	return isValidMatch;
}

/**
 * Checks if the conditional question's answer matches the required condition for the block
 * @param sections - The form sections
 * @param conditionalDefinition - The metadata conditionalDefinitions of the current block
 * @param answers - The form answers
 * @returns Validation result type of Valid/Invalid/Loop
 */
export const checkQuestionConditionValid = (
	sections: ISection[] | IField[],
	conditionalDefinition: ConditionalDefinition,
	answers: IQuestionAnswer[],
	depth: number = 0,
	layout: IWorkBenchLayout | undefined,
	questionName: string
) => {
	if (!conditionalDefinition?.questionId) {
		return ConditionResult.Valid;
	}
	if (depth > 100) {
		return ConditionResult.Loop;
	}

	// Ultra-light cache key for condition check
	const conditionIndicator = conditionalDefinition.questionId + '_' + (conditionalDefinition.operator || 'any');
	const cacheKey = lightKey('condition', conditionIndicator, questionName, depth.toString(), answersSignature(answers));
	if (conditionCache.has(cacheKey)) {
		return conditionCache.get(cacheKey)!;
	}

	const operator = conditionalDefinition.operator ?? ConditionOperators.Any;

	const conditionQuestionId = conditionalDefinition.questionId;
	const matchingAnswer = answers.find(a => a.questionName === conditionQuestionId);
	if (!matchingAnswer || (matchingAnswer?.answer && Object.keys(matchingAnswer.answer)?.length === 0)) {
		if (operator === ConditionOperators.None) return ConditionResult.Invalid;
		if (operator === ConditionOperators.NoneOrUnanswered) return ConditionResult.Valid;
	}

	let notAnswered = false;
	let isValid = false;

	const { questions, allProvidesSelects } = getQuestionsAndSelectsCached(sections, conditionQuestionId);
	const matchingQuestion = questions.find(q => q.name === matchingAnswer?.questionName);
	const subQuestionId = conditionalDefinition.subQuestionId;
	const subQuestions = matchingQuestion?.metadataJson?.provides?.subQuestions as ProvidesSubQuestion;
	const matchingSubQuestion = subQuestions?.find(q => q.id === subQuestionId);
	const questionType = matchingQuestion?.type;

	// Make sure that the condition this condition depends on is valid (validception)
	const isValidMatch = checkMatchConditionValid(questions, conditionQuestionId, sections, answers, depth, layout, questionName);
	if (isValidMatch === ConditionResult.Invalid && operator === ConditionOperators.NoneOrUnanswered) {
		const result = ConditionResult.Valid;
		conditionCache.set(cacheKey, result);
		return result;
	}
	// Since this technically means it is unanswered
	else if (isValidMatch === ConditionResult.Invalid) {
		const result = ConditionResult.Invalid;
		conditionCache.set(cacheKey, result);
		return result;
	} else if (isValidMatch === ConditionResult.Loop) {
		const result = ConditionResult.Loop;
		conditionCache.set(cacheKey, result);
		return result;
	}

	// Set isValid based on the base operator condition of 'any'
	switch (questionType) {
		// Subscription is validated by its planKey only currently
		case 'Subscription':
			const subscriptionAnswer: string = tryParseJSONObject(matchingAnswer?.answer?.['answerJson'], [])?.['planKey'];
			if (!subscriptionAnswer) notAnswered = true;
			isValid = conditionalDefinition.selects?.includes(subscriptionAnswer);
			break;
		case TICKET_BLOCK_TYPE:
			const ticketAnswer = tryParseJSONObject(matchingAnswer?.answer?.['answerJson'], []);
			const ticketAnswerMatch = matchingSubQuestion?.id ? ticketAnswer?.[matchingSubQuestion.id] : undefined;
			if (!ticketAnswerMatch) notAnswered = true;
			isValid = conditionalDefinition.selects?.includes(ticketAnswerMatch);
			break;

		// Multiple choice use a json array and subsequently need to be validated differently
		case FormQuestionTypes.MultiChoice:
		case FormQuestionTypes.MultiChoiceWithOther:
			const parsedMultiChoiceAnswer = tryParseJSONArray(matchingAnswer?.answer?.['answerJson'], []);
			const mappedAnswerIds = parsedMultiChoiceAnswer.map(a => allProvidesSelects.find(c => c.name === a)?.id);
			notAnswered = parsedMultiChoiceAnswer.length === 0;
			isValid = validateMultiChoiceSelects(conditionalDefinition, mappedAnswerIds);
			break;
		case FormQuestionTypes.SingleChoiceRating:
		case FormQuestionTypes.SingleChoice:
		case FormQuestionTypes.SingleChoiceWithOther:
			const singleChoiceAnswer = matchingAnswer?.answer?.['answerString'];

			const matchingChoice = allProvidesSelects?.find(c => singleChoiceAnswer === c.name);
			isValid = matchingChoice?.id ? conditionalDefinition.selects?.includes(matchingChoice.id) : false;
			if (!singleChoiceAnswer) notAnswered = true;
			break;
		case FormQuestionTypes.MatrixScaleChoice:
			const parsedMatrixAnswer = tryParseJSONObject(matchingAnswer?.answer?.['answerJson'], {});
			const mappedMatrixAnswerIds = Object.entries(parsedMatrixAnswer)?.map(([key, value]) => {
				return {
					questionName: key,
					answerId: matchingSubQuestion?.selects?.find(c => c.name === value)?.id
				};
			});
			if (Object.entries(parsedMatrixAnswer).length === 0 || (matchingSubQuestion?.name && !parsedMatrixAnswer[matchingSubQuestion.name]))
				notAnswered = true;
			isValid = conditionalDefinition.selects?.some(s => {
				const match = mappedMatrixAnswerIds.find(a => a.answerId === s && a.questionName === matchingSubQuestion?.name);
				return !!match;
			});

			break;
		case 'CreatePerson':
			const parsed = tryParseJSONObject<any>(matchingAnswer?.answer?.answerJson, {});
			const subQuestionType = matchingSubQuestion?.type;
			switch (subQuestionType) {
				case 'link':
					isValid = (conditionalDefinition?.record ?? []).some(c => {
						const contactFieldData = conditionalDefinition?.subQuestionId
							? parsed.contacts?.[0]?.[conditionalDefinition.subQuestionId]
							: undefined;
						const contactFieldDataArray = ensureArray(contactFieldData);
						if (contactFieldDataArray.length === 0) {
							notAnswered = true;
							return false;
						} else
							return contactFieldDataArray.some(f => {
								return f.entity?.id === c.id && f.entity.type === c.type;
							});
					});
					break;
				case 'select':
					const contactFieldSelects = conditionalDefinition?.subQuestionId
						? (parsed.contacts?.[0]?.[conditionalDefinition.subQuestionId] ?? [])
						: [];
					if (contactFieldSelects.length === 0) {
						notAnswered = true;
						isValid = false;
					} else {
						isValid = validateMultiChoiceSelects(conditionalDefinition, contactFieldSelects);
					}
					break;
				default:
					if (conditionalDefinition.selects?.some((s: any) => s.fieldId && s.id && s.type)) {
						// Legacy condition: selects can't look like this via ui
						isValid = (conditionalDefinition?.selects as unknown as LegacyJsonConditionType)?.some(c => {
							const contactFieldData = parsed.contacts?.[0]?.[c.fieldId];
							if (!contactFieldData) notAnswered = true;
							return contactFieldData && contactFieldData.entity?.id === c.id && contactFieldData.entity.type === c.type;
						});
					} else isValid = false; // Something is wrong if we reach this
					break;
			}
			break;
		default:
			// Find first non empty answer
			// answer = Object.keys(match?.answer ?? {})?.filter(k => !strIsNullOrEmpty(match?.answer?.[k]))?.[0];
			// If you are here we don't actually support your question type yet so I'm not sure how you are here
			break;
	}

	let result: ConditionResult;
	switch (operator) {
		case ConditionOperators.None:
			result = !isValid && !notAnswered ? ConditionResult.Valid : ConditionResult.Invalid;
			break;
		case ConditionOperators.NoneOrUnanswered:
			result = !isValid || notAnswered ? ConditionResult.Valid : ConditionResult.Invalid;
			break;
		case ConditionOperators.Any:
		default:
			result = isValid ? ConditionResult.Valid : ConditionResult.Invalid;
			break;
	}

	// Cache the result before returning
	conditionCache.set(cacheKey, result);
	return result;
};

export function getQuestionsAndSelects(
	sections: ISection[] | IField[],
	questionId: string
): { questions: QuestionsType[]; allProvidesSelects: ProvidesSelect } {
	const questions = findAllFormQuestionSelects(sections)?.filter(q => q.name === questionId);
	const allProvidesSelects: ProvidesSelect = filterEmpty(questions?.flatMap(q => (q.metadataJson as any)?.provides?.select));
	return { questions, allProvidesSelects };
}

function isTypedObject(arg: any): arg is IField {
	return arg && typeof arg === 'object' && typeof arg.metadataJson === 'string' && typeof arg.type === 'string';
}

/**
 * Traverses form sections and returns any with provides select data or calculates new provides if supported.
 * @param sections - The form sections
 * @returns - Array of form fields/questions
 */
export const findAllFormQuestionSelects = (sections: ISection[] | IField[]) => {
	const SUPPORTED_CONDITIONAL_QUESTIONS = [
		FormQuestionTypes.SingleChoice,
		FormQuestionTypes.SingleChoiceWithOther,
		FormQuestionTypes.MultiChoice,
		FormQuestionTypes.MultiChoiceWithOther,
		FormQuestionTypes.SingleChoiceRating,
		'Subscription',
		'CreatePerson',
		FormQuestionTypes.MatrixScaleChoice,
		TICKET_BLOCK_TYPE
	];
	const questions: QuestionsType[] = [];
	traverse(sections).forEach(function (value) {
		try {
			if (isTypedObject(value) && SUPPORTED_CONDITIONAL_QUESTIONS.includes(value.type)) {
				const parsed: QuestionBlockMetadata | SubscriptionFieldMeta | CreatePersonBlockMetadata = tryParseJSONObject(value.metadataJson, {});
				parsed.provides = parsed.provides || {};
				// Transform the question type choices to match our required provides select structure. Note: Subscription block sets its own provides
				switch (value.type) {
					case FormQuestionTypes.SingleChoiceRating:
						const ratingChoices =
							(parsed as QuestionBlockMetadata).choices
								?.map(choice => ({
									name: choice.displayName || '',
									id: choice.id ?? choice.displayName ?? ''
								}))
								.filter(choice => choice.name && choice.id) || [];
						parsed.provides.select = ratingChoices;
						break;
					case FormQuestionTypes.SingleChoice:
					case FormQuestionTypes.SingleChoiceWithOther:
					case FormQuestionTypes.MultiChoice:
					case FormQuestionTypes.MultiChoiceWithOther:
						const standardChoices =
							(parsed as QuestionBlockMetadata).choices
								?.map(choice => ({
									name: choice.displayName || '',
									id: choice.id ?? choice.value ?? ''
								}))
								.filter(choice => choice.name && choice.id) || [];
						parsed.provides.select = standardChoices;
						break;
					case FormQuestionTypes.MatrixScaleChoice:
						const matrixQuestion = parsed as QuestionBlockMetadata;
						const matrixSelects =
							matrixQuestion.choices?.map(c => ({
								id: c.id ?? c.value,
								name: c.displayName
							})) ?? [];

						matrixQuestion.provides = matrixQuestion.provides || {};
						matrixQuestion.provides.subQuestions = filterEmpty(
							matrixQuestion.questions?.map(q => {
								const id = matrixQuestion.questionIds?.find(question => question.name === q)?.id;
								return {
									name: q,
									id: id ?? q,
									type: 'select',
									selects: matrixSelects
								};
							}) ?? []
						);

						break;
					case 'CreatePerson':
						const CREATE_PERSON_SUPPORTED_FIELD_TYPES = ['select', 'link'];
						const businessFields = (parsed as CreatePersonBlockMetadata).businessFields || {};
						parsed.provides.subQuestions = filterEmpty(
							Object.entries(businessFields)?.map(([key, value]) => {
								if (CREATE_PERSON_SUPPORTED_FIELD_TYPES.includes(value.type)) {
									return {
										name: value.label,
										id: key,
										type: value.type,
										selects: value.options?.map(o => ({ id: o.id, name: o.label }))
									};
								} else return null;
							})
						);
					// These blocks set their own provides data
					case TICKET_BLOCK_TYPE:
					case 'Subscription':
					default:
						break;
				}
				const selectAdded = { ...value, metadataJson: parsed };
				questions.push(selectAdded);
			}
		} catch (e) {
			console.error(`Error finding form questions: ${e}`);
		}
	});
	return questions;
};

/**
 * Finds the parent column of this block to check if it has conditions and adds them to the existing conditions array
 * @param questionName
 * @param layout - Form layout
 * @param existingConditions - Conditions array that we are already aware of
 * @returns
 */
export const findColumnConditions = (questionName: string, layout: IWorkBenchLayout | undefined, existingConditions: ConditionalDefinition[]) => {
	const conditionalArray: ConditionalDefinition[] = [];
	// insert any parent columns into the conditional array since this block now depends on the parent being valid as well
	traverse(layout ?? [])?.forEach(value => {
		if (value?.type === 'columns') {
			traverse(value)?.forEach(innerValue => {
				if (innerValue?.props?.name && innerValue?.props?.name === questionName) {
					const defs = tryParseJSONObject<ConditionalBlockMeta>(value.props?.metadataJson, {})?.conditionalDefinitions;
					const defsArray = filterEmpty(ensureArray(defs)?.filter(c => !conditionalArray.some(ca => ca.conditionId === c?.conditionId)));
					conditionalArray.push(...defsArray);
				}
			});
		}
	});
	const definitionsArray = conditionalArray?.filter(c => !existingConditions.some(existing => existing.conditionId === c.conditionId));

	return definitionsArray ?? [];
};

/**
 * Checks whether a block is valid or not based on its conditional definitions.
 * If not in preview mode or does not contain conditionalDefinitions then always returns Valid
 * @param sections - The forms sections
 * @param conditionalDefinitions - Block's conditionalDefinitions from metadata
 * @param answers - Form answers array
 * @param formRenderMode - To check whether we are designing or running the form
 * @returns Validation result type of Valid/Invalid/Loop
 */
export const renderConditionalCheck = (
	sections: ISection[] | IField[],
	conditionalDefinitions: ConditionalDefinitions | undefined,
	answers: IQuestionAnswer[],
	formRenderMode: FormRenderMode,
	questionName: string,
	layout: IWorkBenchLayout | undefined
): ConditionResult => {
	// Early return for design mode - massive performance gain
	if (formRenderMode === FormRenderMode.design) return ConditionResult.Valid;

	// Generate cache key for this entire render check
	const cacheKey = generateConditionCacheKey(questionName, conditionalDefinitions, answers, formRenderMode);

	// Return cached result if available - this is where the massive performance gain comes from
	if (conditionCache.has(cacheKey)) {
		return conditionCache.get(cacheKey)!;
	}

	const conditionalArray = filterEmpty(ensureArray(conditionalDefinitions));

	// We cannot assume this block's condition is valid if it has no condition anymore
	// we must check it isn't in a column with a potential condition.
	const columnConditions = findColumnConditionsCached(questionName, layout, conditionalArray);
	conditionalArray.push(...columnConditions);

	let result: ConditionResult;

	if (!conditionalArray?.some(c => c.questionId)) {
		result = ConditionResult.Valid;
	} else {
		const results = conditionalArray.map(definition =>
			checkQuestionConditionValid(sections, definition, answers, undefined, layout, questionName)
		);
		if (results.some(r => r === ConditionResult.Loop)) {
			result = ConditionResult.Loop;
		} else {
			result = results.every(r => r === ConditionResult.Valid) ? ConditionResult.Valid : ConditionResult.Invalid;
		}
	}

	// Cache the result for future calls - this dramatically improves performance
	conditionCache.set(cacheKey, result);
	return result;
};

/**
 * Returns all blocks from the form layout
 * @param layout - The form layout/sections
 * @returns
 */
export const getAllBlocksFromLayout = layout => {
	const allBlocks: IField[] = [];
	traverse(layout ?? [])?.forEach(function (value) {
		if (value?.metadataJson && value?.type) {
			if (!allBlocks.some(b => b.name === value.name)) {
				allBlocks.push(value);
			}
		}
	});
	return allBlocks;
};

/**
 * Cached version of getAllBlocksFromLayout for better performance
 */
const getAllBlocksFromLayoutCached = (layout: IWorkBenchLayout | undefined): IField[] => {
	// Ultra-light cache key - just indicate if layout exists
	const layoutIndicator = layout ? 'defined' : 'undefined';
	const layoutKey = lightKey('layout', layoutIndicator);

	if (layoutBlocksCache.has(layoutKey)) {
		return layoutBlocksCache.get(layoutKey)!;
	}

	const result = getAllBlocksFromLayout(layout);
	layoutBlocksCache.set(layoutKey, result);
	return result;
};

export function useConditionEvaluator(fieldProps: IField) {
	const { formRenderMode, sections, getAnswers, layout } = useFormRuntimeContext();

	// Don't use useMemo here - our caching system is more efficient and handles answer changes correctly
	const fieldMeta = tryParseJSONObject<ConditionalBlockMeta>(fieldProps?.metadataJson, {});
	// If there is a condition check it matches the required value before rendering the question
	const conditionValid = renderConditionalCheck(
		sections,
		fieldMeta.conditionalDefinitions,
		getAnswers() ?? [],
		formRenderMode,
		fieldProps?.name,
		layout
	);
	return conditionValid === ConditionResult.Valid;
} /**
 * Filters out answers that belong to an invalid conditional question
 * @param survey -
 * @param answers - Array of submitted answers
 * @returns
 */
export const filterOutConditionals = (layout: IWorkBenchLayout | undefined, answers: IQuestionAnswer[], formSections?: ISection[]) => {
	if (!layout || !answers) return answers || [];
	const sections = formSections ?? layout?.layouts?.[0]?.props?.sections ?? [];
	const toDeleteConditionals: string[] = [];

	// Use cached blocks lookup for better performance
	const allBlocks = getAllBlocksFromLayoutCached(layout).map(block => ({
		...block,
		metadataJson: typeof block.metadataJson === 'string' ? JSON.parse(block.metadataJson) : block.metadataJson
	}));

	for (const q of allBlocks) {
		const conditionalArray: ConditionalDefinitions = ensureArray(q.metadataJson?.conditionalDefinitions);

		// Use cached column conditions lookup
		const columnConditions = findColumnConditionsCached(q.name, layout, conditionalArray);
		conditionalArray.push(...columnConditions);

		if (
			conditionalArray.some(c => c.questionId) &&
			!conditionalArray.every(c => checkQuestionConditionValid(sections, c, answers, undefined, layout, q.name) === ConditionResult.Valid)
		) {
			toDeleteConditionals.push(q.name);
		}
	}
	return answers.filter(answer => !toDeleteConditionals.includes(answer.questionName));
};
